import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, AlertTriangle, ShieldCheck, ChevronDown, Check, X } from "lucide-react";

/**
 * Switches which model narrates the score.
 *
 * The point being demonstrated is that the score is deterministic and the model
 * only writes the words about it. So this control deliberately re-renders
 * nothing except the explanation card: the number, tier, gauge and signal bars
 * must stay visibly still while the narration changes underneath them.
 *
 * Only providers the backend reports as `configured` are offered, so a missing
 * key shows up as one fewer option rather than a button that fails.
 */

const LABELS = {
    claude: "Claude",
    gemini: "Gemini",
    template: "Template",
};

export function ModelSwitch({ providers, active, onSelect, busy }) {
    if (!providers?.length || providers.length < 2) return null;

    return (
        <div
            className="inline-flex items-center gap-0.5 p-0.5 rounded-xl bg-white/[0.04] border border-white/10"
            role="radiogroup"
            aria-label="Explanation model"
            data-testid="model-switch"
        >
            {providers.map((p) => {
                const isActive = p.name === active;
                return (
                    <button
                        key={p.name}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        disabled={busy}
                        onClick={() => onSelect(p.name)}
                        title={p.model}
                        className={`relative px-3 py-1.5 rounded-[10px] text-xs font-medium transition-colors min-h-[32px]
                            disabled:opacity-60 disabled:cursor-wait
                            ${isActive ? "text-text-primary" : "text-text-secondary hover:text-text-primary"}`}
                        data-testid={`model-switch-${p.name}`}
                    >
                        {isActive && (
                            <motion.span
                                layoutId="model-switch-active"
                                className="absolute inset-0 rounded-[10px] bg-purple-soft/15 border border-purple-soft/30"
                                transition={{ type: "spring", stiffness: 380, damping: 30 }}
                            />
                        )}
                        <span className="relative z-10">{LABELS[p.name] || p.name}</span>
                    </button>
                );
            })}
        </div>
    );
}

function formatTokens(usage) {
    if (!usage) return null;
    const i = Number(usage.input_tokens);
    const o = Number(usage.output_tokens);
    if (!Number.isFinite(i) && !Number.isFinite(o)) return null;
    const parts = [];
    if (Number.isFinite(i)) parts.push(`${i.toLocaleString("en-IN")} in`);
    if (Number.isFinite(o)) parts.push(`${o.toLocaleString("en-IN")} out`);
    const t = Number(usage.thinking_tokens);
    if (Number.isFinite(t) && t > 0) parts.push(`${t.toLocaleString("en-IN")} thinking`);
    return `${parts.join(" / ")} tokens`;
}

/**
 * Provenance line under the narrative.
 *
 * `generated_by` is the honest field: when a provider errors the backend serves
 * the template narrative and reports `template-fallback-after-<provider>-error`.
 * Surfacing that rather than hiding it matters — claiming a model wrote
 * something it did not is exactly the sort of thing this product must not do.
 */
export function GenerationMeta({ generatedBy, liveGeneration, error }) {
    if (error) {
        return (
            <p
                className="mt-4 pt-3 border-t border-white/5 text-[11px] text-amber-builder flex items-center gap-1.5"
                data-testid="generation-meta"
            >
                <AlertTriangle size={11} className="shrink-0" />
                {error}
            </p>
        );
    }

    if (!generatedBy) return null;

    // `template-fallback-after-<provider>-error` means a model call failed.
    // Plain `template-fallback` is the template deliberately chosen, which is
    // a normal state and must not be dressed up as an error.
    const fellBack = /fallback-after-/i.test(generatedBy);
    const seconds = Number.isFinite(Number(liveGeneration?.latency_ms))
        ? `${(Number(liveGeneration.latency_ms) / 1000).toFixed(1)}s`
        : null;
    const tokens = formatTokens(liveGeneration?.usage);

    const bits = [generatedBy, seconds, tokens].filter(Boolean);

    return (
        <p
            className={`mt-4 pt-3 border-t border-white/5 text-[11px] font-mono break-words ${
                fellBack ? "text-amber-builder/90" : "text-text-secondary/60"
            }`}
            data-testid="generation-meta"
        >
            {fellBack && <AlertTriangle size={11} className="inline mr-1.5 -mt-0.5" />}
            {bits.join(" · ")}
            {fellBack && (
                <span className="block mt-1 not-italic text-amber-builder/70">
                    That model was unreachable, so this is the deterministic template narrative.
                </span>
            )}
        </p>
    );
}

/** Overlay shown on the explanation card while a model is regenerating. */
export function RegeneratingVeil({ provider }) {
    return (
        <div
            className="absolute inset-0 z-10 rounded-2xl bg-void/60 backdrop-blur-[2px] flex items-center justify-center"
            data-testid="regenerating-veil"
        >
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-elevated/90 border border-white/10">
                <Loader2 size={14} className="text-purple-soft animate-spin shrink-0" />
                <span className="text-xs text-text-secondary">
                    Asking {LABELS[provider] || provider}…
                </span>
            </div>
        </div>
    );
}

const CHECK_LABELS = {
    required_fields: "All required fields present",
    factor_coverage: "Every scored signal explained",
    no_invented_numbers: "No invented numbers",
    no_invented_sources: "No invented data sources",
    negatives_not_omitted: "Weak signals not buried",
    zero_weight_not_credited: "Unweighted signals not credited",
    score_not_contradicted: "Score not contradicted",
    no_financial_advice: "No financial advice given",
    no_injection_compliance: "Ignored instructions in user data",
};

/**
 * The faithfulness gate, shown in the product rather than only in CI.
 *
 * Nine deterministic checks confirm every claim in the narrative traces back to
 * the attribution vector the scoring engine produced. An explanation that has
 * been verified is worth more than one that has not, so this is a feature
 * surface, not a debug panel — which is also why a failure is stated plainly
 * instead of being hidden.
 */
export function FaithfulnessPanel({ faithfulness }) {
    const [open, setOpen] = useState(false);

    // Absent on the static payload and whenever the evaluation itself failed.
    // Rendering nothing is correct: claiming "verified" without having checked
    // would be the exact failure this panel exists to catch.
    if (!faithfulness || !faithfulness.checks) return null;

    const entries = Object.entries(faithfulness.checks);
    const total = entries.length;
    const passedCount = entries.filter(([, failures]) => !failures || failures.length === 0).length;
    const allPassed = faithfulness.passed && passedCount === total;

    return (
        <div className="mt-3" data-testid="faithfulness-panel">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors min-h-[28px] ${
                    allPassed
                        ? "border-green-pioneer/30 bg-green-pioneer/10 text-green-pioneer hover:bg-green-pioneer/15"
                        : "border-amber-builder/30 bg-amber-builder/10 text-amber-builder hover:bg-amber-builder/15"
                }`}
                data-testid="faithfulness-toggle"
            >
                {allPassed ? <ShieldCheck size={12} /> : <AlertTriangle size={12} />}
                Faithfulness {passedCount}/{total} checks passed
                <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="faithfulness-detail">
                    <p className="text-[10px] text-text-secondary/60 leading-relaxed mb-2">
                        Deterministic checks run against the attribution vector before this
                        narrative is shown. The model writes the words; it does not decide the score.
                    </p>
                    <ul className="space-y-1">
                        {entries.map(([name, failures]) => {
                            const ok = !failures || failures.length === 0;
                            return (
                                <li key={name} className="flex items-start gap-2 text-[11px]">
                                    {ok ? (
                                        <Check size={11} className="mt-0.5 shrink-0 text-green-pioneer" />
                                    ) : (
                                        <X size={11} className="mt-0.5 shrink-0 text-amber-builder" />
                                    )}
                                    <span className={ok ? "text-text-secondary/70" : "text-amber-builder"}>
                                        {CHECK_LABELS[name] || name}
                                        {!ok && (
                                            <span className="block text-amber-builder/80 break-words">
                                                {failures.join("; ")}
                                            </span>
                                        )}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
